import { useCallback, useState } from 'react';

import { CircularProgress, MenuItem, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { ControlledInputFile } from '../../components/forms';
import { StyledLabel } from '../../components/forms/styles';
import { StyledButton, StyledFooter, StyledForm, StyledTypography } from '../ClassModal/ClassForm.styles';
import ReportProblemContainer from './ReportProblemContainer';
import {
    StyledAttachmentError,
    StyledAttachmentsList,
    StyledContainer,
    StyledControlledInput,
    StyledControlledSelect,
    StyledFieldset,
    StyledFormContainer,
    StyledFormError,
    StyledFormSuccess,
    StyledInputContainer,
    StyledLink
} from './ReportProblemForm.styles';
import { PRIVACY_POLICY } from '../../constants/urls';
import { useAppMessaging } from '../../hooks/useAppMessaging';

// Copied from /functions/problem-report/types.ts
export enum ProblemType {
    JoinClass = 'join_class',
    AudioQuality = 'audio_quality',
    VideoQuality = 'video_quality',
    ScreenSharing = 'screen_sharing',
    Recording = 'recording',
    DialIn = 'dial_in',
    Other = 'other'
}

// Can't place it to .styles file, colors does not exists on theme
const StyledSpinner = styled(CircularProgress)`
    color: ${(props) => props.theme.colors.white};
`;

const ReportProblemForm = () => {
    const { t } = useTranslation('report_problem');
    const { t: tIndexClient } = useTranslation('index_client');
    const { sendMessage } = useAppMessaging();

    const { control, reportProblem, handleSubmit, setValue, watch, onCancel, handleSuccess, formState } =
        useContainer(ReportProblemContainer);

    const attachments: File[] = watch('attachments');

    const [reportState, setReportState] = useState<'started' | 'finished' | 'error'>();

    const onSubmit = async () => {
        setReportState('started');

        try {
            await reportProblem();
            setReportState('finished');
            handleSuccess();
        } catch (e: any) {
            setReportState('error');

            sendMessage({ func: 'error', data: e.message });
        }
    };

    const removeAttachment = useCallback(
        (fileName: string) => {
            setValue(
                'attachments',
                attachments.filter((attachment) => attachment.name !== fileName),
                { shouldValidate: true }
            );
        },
        [setValue, attachments]
    );

    const submitDisabled = reportState === 'started' || !formState.isValid;

    return (
        <StyledFormContainer>
            <StyledTypography variant="h5">{t('title')}</StyledTypography>

            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <StyledFieldset>
                    <StyledInputContainer>
                        <StyledControlledSelect
                            control={control}
                            name="problemType"
                            label={t('problem-type-title')}
                            required
                        >
                            <MenuItem value={ProblemType.JoinClass} selected>
                                {t('problem-type-join-class')}
                            </MenuItem>
                            <MenuItem value={ProblemType.AudioQuality}>{t('problem-type-audio-problem')}</MenuItem>
                            <MenuItem value={ProblemType.VideoQuality}>{t('problem-type-video-problem')}</MenuItem>
                            <MenuItem value={ProblemType.ScreenSharing}>{t('problem-type-screen-sharing')}</MenuItem>
                            <MenuItem value={ProblemType.Recording}>{t('problem-type-recording')}</MenuItem>
                            <MenuItem value={ProblemType.DialIn}>{t('problem-type-dial-in')}</MenuItem>
                            <MenuItem value={ProblemType.Other}>{t('problem-type-others')}</MenuItem>
                        </StyledControlledSelect>
                    </StyledInputContainer>

                    <StyledInputContainer>
                        <StyledControlledInput
                            control={control}
                            name="description"
                            label={t('description-title')}
                            multiline
                            rows={5}
                            required
                        />
                    </StyledInputContainer>

                    <StyledInputContainer>
                        <StyledControlledInput
                            control={control}
                            name="organization"
                            label={t('organization-title')}
                            placeholder={t('organization-placeholder')}
                        />
                    </StyledInputContainer>

                    <StyledInputContainer>
                        <StyledControlledInput
                            control={control}
                            type="datetime-local"
                            name="occurence"
                            label={t('time-of-occurence')}
                        />
                    </StyledInputContainer>

                    <StyledLabel>{t('attachments-title')}</StyledLabel>

                    <ControlledInputFile
                        control={control}
                        name="attachments"
                        title={t('attachments-button')}
                        inputProps={{
                            multiple: true
                        }}
                    />

                    <StyledAttachmentsList attachments={attachments} onDeleteItem={removeAttachment} />
                    {formState.errors?.attachments?.message && (
                        <StyledAttachmentError>{formState.errors.attachments.message}</StyledAttachmentError>
                    )}
                </StyledFieldset>

                <StyledContainer>
                    <Typography>{t('contact-title')}</Typography>
                </StyledContainer>
                <StyledFieldset sx={{ borderBottom: 0 }}>
                    <StyledInputContainer>
                        <StyledControlledInput
                            control={control}
                            name="contactName"
                            label={t('contact-name')}
                            placeholder={t('contact-name-placeholder')}
                            required
                        />
                    </StyledInputContainer>

                    <StyledInputContainer>
                        <StyledControlledInput
                            control={control}
                            name="contactEmail"
                            label={t('contact-email')}
                            placeholder={t('contact-email-placeholder')}
                            required
                        />
                    </StyledInputContainer>
                </StyledFieldset>

                <StyledContainer sx={{ display: 'inline-block' }}>
                    <Trans t={t} i18nKey="agree-to-terms">
                        text <StyledLink href={PRIVACY_POLICY} target="_blank" />
                    </Trans>
                </StyledContainer>

                <StyledFooter>
                    <StyledButton onClick={() => onCancel()} variant="outlined">
                        {tIndexClient('cancel')}
                    </StyledButton>

                    <StyledButton onClick={() => onSubmit()} variant="contained" disabled={submitDisabled}>
                        {reportState === 'started' ? <StyledSpinner size={18} /> : tIndexClient('submit')}
                    </StyledButton>
                </StyledFooter>

                {reportState === 'error' && <StyledFormError>{t('errors.form-error')}</StyledFormError>}
                {reportState === 'finished' && <StyledFormSuccess>{t('form-success')}</StyledFormSuccess>}
            </StyledForm>
        </StyledFormContainer>
    );
};

export default ReportProblemForm;
